'use client'

/**
 * Шаг 4 импорта — предпросмотр (Intelligence Terminal §Tables, §Buttons).
 */

import { ArrowLeft, DownloadSimple } from '@phosphor-icons/react'
import type { ImportPreviewResponse } from '../import-wizard.types'
import { cn } from '@/lib/utils'
import { useT } from '@/lib/i18n'

interface Props {
  preview: ImportPreviewResponse | null
  onNext: () => void
  onBack: () => void
  loading: boolean
}

export function StepPreview({ preview, onNext, onBack, loading }: Props) {
  const t = useT()
  if (!preview) return null
  const rows = Array.isArray(preview.rows) ? preview.rows : []
  const columns = rows.length > 0 ? Object.keys(rows[0] ?? {}) : []

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <p className="text-[13px] font-medium text-[var(--text-primary)]">
            {t('importWizard.preview.title')}
          </p>
          <p className="mt-0.5 font-mono text-[11px] tabular-nums text-[var(--text-tertiary)]">
            {t('importWizard.preview.firstRows', { total: preview.stats.totalRows })}
          </p>
        </div>
        <div className="flex flex-wrap gap-3 font-mono text-[11px] tabular-nums">
          <span className="text-[var(--text-tertiary)]">
            {t('importWizard.preview.fields')}{' '}
            <span className="font-medium text-[var(--text-primary)]">
              {preview.stats.matchedFields}
            </span>
          </span>
          <span className="text-[var(--text-tertiary)]">
            {t('importWizard.preview.errors')}{' '}
            <span className="font-medium text-risk-high">
              {preview.stats.errors}
            </span>
          </span>
          <span className="text-[var(--text-tertiary)]">
            {t('importWizard.preview.warnings')}{' '}
            <span className="font-medium text-risk-medium">
              {preview.stats.warnings}
            </span>
          </span>
        </div>
      </div>

      <div className="overflow-hidden rounded-[4px] border border-[var(--border-subtle)] bg-[var(--bg-surface)] max-h-[360px] overflow-y-auto">
        <table className="w-full border-collapse">
          <thead className="sticky top-0">
            <tr className="border-b border-[var(--border-default)] bg-[var(--bg-subtle)]">
              {columns.map((col) => (
                <th
                  key={col}
                  className="whitespace-nowrap px-3 py-2 text-left text-[10px] font-medium uppercase tracking-[0.08em] text-[var(--text-secondary)]"
                >
                  {col}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {rows.map((row, i) => (
              <tr
                key={i}
                className="border-b border-[var(--border-subtle)] hover:bg-[var(--bg-subtle)] last:border-b-0"
              >
                {columns.map((col) => (
                  <td
                    key={col}
                    className="max-w-[200px] truncate whitespace-nowrap px-3 py-1.5 font-mono text-[12px] tabular-nums text-[var(--text-primary)]"
                  >
                    {row[col] !== null && row[col] !== undefined ? String(row[col]) : '—'}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <div className="flex items-center justify-between border-t border-[var(--border-subtle)] pt-4">
        <button
          type="button"
          onClick={onBack}
          className="inline-flex items-center gap-1.5 rounded-[4px] border border-[var(--border-default)] bg-transparent px-3 py-1.5 text-[12px] font-medium text-[var(--text-secondary)] transition-colors duration-150 hover:border-[var(--border-strong)] hover:text-[var(--text-primary)]"
        >
          <ArrowLeft size={12} />
          {t('common.action.back')}
        </button>
        <button
          type="button"
          onClick={onNext}
          disabled={loading}
          className={cn(
            'inline-flex items-center gap-1.5 rounded-[4px] px-3 py-1.5 text-[12px] font-medium',
            'bg-risk-low text-fg-on-risk',
            'transition-colors duration-150',
            'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--accent-data)] focus-visible:ring-offset-2',
            'disabled:opacity-40 disabled:cursor-not-allowed',
          )}
        >
          {loading
            ? t('importWizard.preview.committing')
            : t('importWizard.preview.commit', { count: preview.stats.totalRows })}
          <DownloadSimple size={12} />
        </button>
      </div>
    </div>
  )
}
